import { Box, Typography, Paper, Chip, Grid, CircularProgress, FormControlLabel, Switch, TextField } from '@mui/material';
import { useApp } from '../context/AppContext';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupIcon from '@mui/icons-material/Group';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaidIcon from '@mui/icons-material/Paid';

const Dashboard = () => {
  const { user } = useApp();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBalance: 0,
    activeAccounts: 0,
    deliveryAmount: 0,
    collectedAmount: 0
  });
  const [logs, setLogs] = useState([]);
  const [showAllTime, setShowAllTime] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  });

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, showAllTime]);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);

      // Use date range for filtering
      const startDate = dateRange.start.toISOString();
      const endDate = dateRange.end.toISOString();

      // Fetch logs - show last 50 updates
      const { data: logsData, error: logsError } = await supabase
        .from('logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (logsError) throw logsError;

      // Fetch entries with date range
      const query = supabase
        .from('entries')
        .select('amount, collection, balance');
      
      if (!showAllTime) {
        query.gte('date', startDate).lte('date', endDate);
      }
      
      const { data: entries, error: entriesError } = await query;

      if (entriesError) throw entriesError;

      // Fetch active accounts count
      const { count, error: accountsError } = await supabase
        .from('accounts')
        .select('id', { count: 'exact' });

      if (accountsError) throw accountsError;

      // Calculate stats
      const totalBalance = entries?.reduce((sum, entry) => sum + (entry.balance || 0), 0) || 0;
      const deliveryAmount = entries?.reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0;
      const collectedAmount = entries?.reduce((sum, entry) => sum + (entry.collection || 0), 0) || 0;

      setStats({
        totalBalance,
        activeAccounts: count || 0,
        deliveryAmount,
        collectedAmount
      });

      setLogs(logsData || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const statCards = [
    {
      title: 'Total Balance',
      value: stats.totalBalance,
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40, color: '#2196f3' }} />,
      color: '#e3f2fd'
    },
    {
      title: 'Total Active Accounts',
      value: stats.activeAccounts,
      icon: <GroupIcon sx={{ fontSize: 40, color: '#4caf50' }} />,
      color: '#e8f5e9'
    },
    {
      title: 'Total Delivery Amount',
      value: stats.deliveryAmount,
      icon: <LocalShippingIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      color: '#fff3e0'
    },
    {
      title: 'Total Collected Amount',
      value: stats.collectedAmount,
      icon: <PaidIcon sx={{ fontSize: 40, color: '#e91e63' }} />,
      color: '#fce4ec'
    }
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={dateRange.start}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, start: newValue }))}
              disabled={showAllTime}
              slots={{
                textField: TextField
              }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.end}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, end: newValue }))}
              disabled={showAllTime}
              slots={{
                textField: TextField
              }}
            />
          </LocalizationProvider>
          <FormControlLabel
            control={
              <Switch
                checked={showAllTime}
                onChange={(e) => setShowAllTime(e.target.checked)}
              />
            }
            label={showAllTime ? "All Time" : "Date Range"}
          />
        </Box>
      </Box>

      {/* First Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.slice(0, 2).map((card, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Paper 
              sx={{ 
                p: 3,
                backgroundColor: card.color,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              {card.icon}
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>{card.title}</Typography>
                <Typography variant="h4">
                  {typeof card.value === 'number' && card.title !== 'Total Active Accounts' 
                    ? formatCurrency(card.value)
                    : card.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Second Row */}
      <Grid container spacing={3}>
        {statCards.slice(2).map((card, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Paper 
              sx={{ 
                p: 3,
                backgroundColor: card.color,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              {card.icon}
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>{card.title}</Typography>
                <Typography variant="h4">{formatCurrency(card.value)}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>Samohealth Updates</Typography>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Showing last 50 updates</Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {logs.length === 0 ? (
            <Typography color="text.secondary">No recent updates</Typography>
          ) : (
            logs.map((log) => (
              <Box 
                key={log.id} 
                sx={{ 
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2
                }}
              >
                <Chip 
                  label={log.type} 
                  sx={{ 
                    backgroundColor: 
                      log.type === 'Delivery' ? '#90EE90' : 
                      log.type === 'Payment' ? '#FFB6C1' : 
                      '#FFFF00',
                    minWidth: '80px',
                    mt: 0.5
                  }} 
                />
                <Box>
                  <Typography>{log.message}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(log.created_at).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard; 